import { useLogto } from '@logto/react';
import React, { createContext, useContext, useEffect } from 'react';

const LogtoContext = createContext<{
  signOut: () => Promise<void>;
} | null>(null);

export const useLogtoContext = () => {
  const context = useContext(LogtoContext);
  if (!context) {
    throw new Error('useLogtoContext must be used within LogtoWrapper');
  }
  return context;
};

export const LogtoWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { signOut, getAccessToken, isAuthenticated } = useLogto();

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Logto signOut failed:', error);
    } finally {
      localStorage.removeItem('logto_access_token');
      localStorage.removeItem('hasura_user_id');
      localStorage.removeItem('hasura_role');
      localStorage.removeItem('user_roles');
      localStorage.removeItem('current_user');

      if (window.location.pathname !== '/user/login') {
        window.location.replace('/user/login');
      }
    }
  };

  // 周期刷新 access token，避免长时间后本地 token 过期
  useEffect(() => {
    let timer: number | undefined;
    const RESOURCE = 'https://hasura.api';

    const refresh = async () => {
      try {
        if (!isAuthenticated) return;
        const token = await getAccessToken(RESOURCE);
        if (token) localStorage.setItem('logto_access_token', token);
      } catch (e) {
        // 忽略，401 将由 Apollo errorLink 处理
      }
    };

    // 首次尝试刷新
    refresh();

    // 定时刷新（每 4 分钟；Logto 默认 access token 10-15m，有刷新足够）
    timer = window.setInterval(refresh, 4 * 60 * 1000);

    // 页面重新可见时也刷新一次
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      if (timer) window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [getAccessToken, isAuthenticated]);

  return (
    <LogtoContext.Provider value={{ signOut: handleLogout }}>
      {children}
    </LogtoContext.Provider>
  );
};
